import { TextTokenElement } from "./textToken";

const template = document.createElement("template");
template.innerHTML = `
      <style>
      :host {
          display: block;
      }
      .container {
        padding: 0;
        box-sizing: border-box;
        border-radius: 4px;
        min-width: 20px;
        height: 2.6rem;
        transition: box-shadow 200ms;
        border: 1px solid #ccc;
        display: flex;
        align-items: center;
      }

      .container.focused {
        outline: none;
        border: 1px solid var(--color-atos-main);
        box-shadow: 0px 0px 5px rgba(82,168,236,.8);
      }

      .container.is-pending {
        transition: box-shadow 200ms;
        padding-left: 2px;
        border: 1px solid #C09853;
        border-left: 5px solid #C09853;
      }
      .container.is-pending.focused {
          border: 1px solid var(--color-warning);
          border-left: 5px solid var(--color-warning);
          box-shadow: 0 0 5px var(--color-warning);
      }
      .container.has-error {
        transition: box-shadow 200ms;
        padding-left: 2px;
        border: 1px solid #B94A48;
        border-left: 5px solid #B94A48;
        border-color: #B94A48;
      }
      .container.has-error.focused {
        border: 1px solid var(--color-error);
        border-left: 5px solid var(--color-error);
        box-shadow: 0px 0px 5px var(--color-error);
      }
      .container.read-only {
        background-color: #eceeef;
      }

      #text-entry {
          outline: none;
          border: none;
          flex-grow: 1;
          margin:0;
          padding: 0 0 0 0.5rem;
      }
      s6-token {
          padding-left: 0.25rem;
      }
      #tokens {
        display:flex;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none;
      }
      #tokens.read-only {
        max-width: 100%;
      }
      #tokens::-webkit-scrollbar {
        display: none;  /* Safari and Chrome */
      }
      </style>
      <div class="container">
      <span id="tokens">
      </span>
      <slot></slot>
      </div>
    `;

export class TokenizedElement extends HTMLElement {
    private _shadowRoot: ShadowRoot;
    private _data: TokenData[] | string[] | number[];
    private _readOnly: boolean;
    private container: any;
    private _valid: boolean;
    private visited: any;

    constructor() {
        super();
        this._shadowRoot = this.attachShadow({ mode: "open" });
        this._shadowRoot.appendChild(template.content.cloneNode(true));
        this.container = this._shadowRoot.querySelector(".container");
        this._shadowRoot.querySelector("#tokens").addEventListener("removeToken", this.removeToken);
        this._shadowRoot.querySelector("#tokens").addEventListener("wheel", this.scrollH);

        this._shadowRoot.querySelector("#tokens").addEventListener("click", () => {
            this.focus();
        });
        if (this.hasAttribute("isNumeric")) {
            this.setAttribute("allowedPattern", "^\\d+$");
        }
    }

    private connectedCallback() {
        this.upgradeProperty();
    }
    private upgradeProperty() {
        for (const key in this) {
            if (this.hasOwnProperty(key)) {
                let value = this[key];
                delete this[key];
                this[key] = value;
            }
        }
    }

    private scrollH = (evt) => {
        this._shadowRoot.querySelector("#tokens").scrollBy(evt.deltaY, 0);
        evt.preventDefault();
    }

    private removeToken = (evt) => {
        let token: TextTokenElement = evt.target;
        if (this.hasAttribute("isNumeric")) {
            let n = parseInt(token.data, 10);
            this._data.splice((this._data as number[]).indexOf(n), 1);
        } else {
            let yy = (this._data as any[]).indexOf(token.data);
            this._data.splice((this._data as any[]).indexOf(token.data), 1);
        }
        this._shadowRoot.querySelector("#tokens").removeChild(token);
        this.dispatchEvent(new Event("change"));
    }

    private render() {
        this._shadowRoot.querySelector("#tokens").innerHTML = "";
        for (const tokenData of this._data ?? []) {
            let token;
            if ((isString(tokenData))) {
                token = document.createElement("s6-text-token");
                token.innerText = tokenData;
            } else if ((isNumber(tokenData))) {
                token = document.createElement("s6-text-token");
                token.innerText = tokenData;
            } else if (isString(tokenData.text)) {
                token = document.createElement("s6-text-token");
                token.innerText = tokenData.text;
            } else if (tokenData.texts != null) {
                token = document.createElement("s6-composed-text-token");
                token.data = tokenData;
            }
            if (this._readOnly) {
                token.setAttribute("readonly", "");
            }
            this._shadowRoot.querySelector("#tokens").append(token);
        }
        if (this._readOnly) {
            this.container.classList.add("read-only");
            this._shadowRoot.querySelector("#tokens").classList.add("read-only");
        } else {
            this.container.classList.remove("read-only");
            this._shadowRoot.querySelector("#tokens").classList.remove("read-only");
        }
    }

    public set data(data: any[]) {
        this._data = data;
        this.render();
    }

    public get data() {
        if (this._data?.length === 0) {
            return null;
        }
        return this._data;
    }

    public set readOnly(ro: boolean) {
        if (ro) {
            this.setAttribute("readonly", "true");
        } else {
            this.removeAttribute("readonly");
        }
        this._readOnly = ro;
        this.render();
    }

    public set valid(valid: boolean) {
        this._valid = valid ?? true;
        if (this._valid) {
            this.container.classList.remove("has-error");
            this.container.classList.remove("is-pending");
        } else if (this.visited) {
            this.container.classList.add("has-error");
        } else {
            this.container.classList.add("is-pending");
        }
    }

    public set validationMessage(message) {
        if (message == null) {
            this._shadowRoot.querySelector("s6-tooltip")?.remove();
        } else {
            let tooltip = document.createElement("s6-tooltip");
            tooltip.innerHTML = message;
            this._shadowRoot.append(tooltip);
        }
    }

    public focus() {
        this.visited = true;
        this.container.classList.add("focused");
    }

    public blur() {
        this.valid = this._valid;
        this.container.classList.remove("focused");
    }
}

export function initTokenizedWebComponent() {
    window.customElements.define("s6-tokenized", TokenizedElement);
}


export interface TokenData {
    text?: string;
    texts?: string[];
    tooltip?: string;
}
function isString(x: any): x is string {
    return typeof x === "string";
}
function isNumber(x: any): x is number {
    return typeof x === "number";
}